import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Beneficial insects that appear based on plant types
const BENEFICIAL_INSECTS = [
  { id: "bee", emoji: "🐝", name: "Honeybee", attracted_by: ["flower", "sunflower", "lavender", "clover"] },
  { id: "butterfly", emoji: "🦋", name: "Butterfly", attracted_by: ["flower", "milkweed", "zinnia", "marigold"] },
  { id: "ladybug", emoji: "🐞", name: "Ladybug", attracted_by: ["dill", "fennel", "yarrow", "marigold"] },
  { id: "dragonfly", emoji: "🪰", name: "Dragonfly", attracted_by: ["pond", "water"] },
  { id: "hummingbird", emoji: "🐦", name: "Hummingbird", attracted_by: ["salvia", "trumpet", "bee balm", "flower"] },
];

// Growth stages for plant animations
const GROWTH_STAGES = [
  { scale: 0.3, opacity: 0.5, label: "Seedling" },
  { scale: 0.5, opacity: 0.7, label: "Sprouting" },
  { scale: 0.75, opacity: 0.85, label: "Growing" },
  { scale: 1, opacity: 1, label: "Mature" },
];

const LivingGardenOverlay = ({ 
  plants = [], 
  structures = [], 
  isActive = false,
  canvasWidth = 800,
  canvasHeight = 600
}) => {
  const [insects, setInsects] = useState([]);
  const [growingPlants, setGrowingPlants] = useState({});
  const [sparkles, setSparkles] = useState([]);
  const animationRef = useRef(null);

  // Determine which insects should appear based on plants
  useEffect(() => {
    if (!isActive) {
      setInsects([]);
      return;
    }

    const plantNames = plants.map(p => p.plant_name?.toLowerCase() || "");
    const structureNames = structures.map(s => s.name?.toLowerCase() || "");
    const allElements = [...plantNames, ...structureNames];

    const attractedInsects = BENEFICIAL_INSECTS.filter(insect => 
      insect.attracted_by.some(attractor => 
        allElements.some(element => element.includes(attractor))
      )
    );

    // Create multiple instances of attracted insects
    const insectInstances = [];
    attractedInsects.forEach(insect => {
      const count = Math.min(3, Math.floor(Math.random() * 3) + 1);
      for (let i = 0; i < count; i++) {
        insectInstances.push({
          ...insect,
          instanceId: `${insect.id}-${i}`,
          startX: Math.random() * 100,
          startY: Math.random() * 100,
          duration: 8 + Math.random() * 6,
          delay: Math.random() * 3,
        });
      }
    });

    setInsects(insectInstances);
  }, [isActive, plants, structures]);

  // Animate plants growing
  useEffect(() => {
    if (!isActive) {
      setGrowingPlants({});
      return;
    }

    // Initialize all plants at stage 0
    const initialGrowth = {};
    plants.forEach((plant, idx) => {
      initialGrowth[plant.id] = 0;
    });
    setGrowingPlants(initialGrowth);

    // Gradually grow plants
    const growInterval = setInterval(() => {
      setGrowingPlants(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(id => {
          if (next[id] < GROWTH_STAGES.length - 1) {
            // Random chance to grow
            if (Math.random() > 0.7) {
              next[id] = Math.min(next[id] + 1, GROWTH_STAGES.length - 1);
            }
          }
        });
        return next;
      });
    }, 2000);

    return () => clearInterval(growInterval);
  }, [isActive, plants]);

  // Create sparkle effects
  useEffect(() => {
    if (!isActive) {
      setSparkles([]);
      return;
    }

    const sparkleInterval = setInterval(() => {
      const newSparkle = {
        id: Date.now(),
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 10 + 5,
      };
      
      setSparkles(prev => [...prev.slice(-10), newSparkle]);
    }, 1500);

    return () => clearInterval(sparkleInterval);
  }, [isActive]);

  if (!isActive) return null;

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-30">
      {/* Sparkles */}
      <AnimatePresence>
        {sparkles.map(sparkle => (
          <motion.div
            key={sparkle.id}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: [0, 1, 0], scale: [0, 1, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2 }}
            className="absolute text-yellow-400"
            style={{ left: `${sparkle.x}%`, top: `${sparkle.y}%` }}
          >
            ✨
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Flying Insects */}
      {insects.map(insect => (
        <motion.div
          key={insect.instanceId}
          className="absolute text-2xl"
          initial={{ 
            left: `${insect.startX}%`, 
            top: `${insect.startY}%`,
            opacity: 0 
          }}
          animate={{
            left: [
              `${insect.startX}%`,
              `${(insect.startX + 30) % 100}%`,
              `${(insect.startX + 60) % 100}%`,
              `${(insect.startX + 20) % 100}%`,
              `${insect.startX}%`,
            ],
            top: [
              `${insect.startY}%`,
              `${Math.max(10, (insect.startY - 20))}%`,
              `${Math.min(90, (insect.startY + 15))}%`,
              `${Math.max(10, (insect.startY - 10))}%`,
              `${insect.startY}%`,
            ],
            opacity: [0, 1, 1, 1, 0],
            rotate: [0, 10, -10, 5, 0],
          }}
          transition={{
            duration: insect.duration,
            delay: insect.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          {insect.emoji}
        </motion.div>
      ))}

      {/* Growth indicators on plants */}
      {plants.map(plant => {
        const stage = growingPlants[plant.id] || 0;
        const growth = GROWTH_STAGES[stage];
        
        if (stage === GROWTH_STAGES.length - 1) {
          // Mature plant - show subtle pulse
          return (
            <motion.div
              key={`growth-${plant.id}`}
              className="absolute"
              style={{ 
                left: `${plant.x}%`, 
                top: `${plant.y}%`,
                transform: "translate(-50%, -50%)"
              }}
              animate={{ 
                scale: [1, 1.1, 1],
                opacity: [0.3, 0.5, 0.3]
              }}
              transition={{ 
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <div className="w-16 h-16 rounded-full bg-green-400/30 blur-sm" />
            </motion.div>
          );
        }
        
        return null;
      })}

      {/* Ambient nature sounds indicator */}
      <div className="absolute bottom-4 right-4 bg-white/80 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-2 text-sm">
        <motion.span
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          🌿
        </motion.span>
        <span className="text-green-700 font-medium">Garden Alive!</span>
      </div>
    </div>
  );
};

// Celebration animation when achievements are unlocked
export const AchievementCelebration = ({ achievement, onComplete }) => {
  useEffect(() => {
    const timer = setTimeout(onComplete, 4000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      {/* Confetti */}
      {[...Array(30)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute text-2xl"
          initial={{ 
            top: "50%", 
            left: "50%",
            opacity: 1 
          }}
          animate={{
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            opacity: 0,
            rotate: Math.random() * 720,
          }}
          transition={{ duration: 2, delay: Math.random() * 0.5 }}
        >
          {["🌱", "🌿", "🌸", "🌻", "🍃", "✨", "⭐"][Math.floor(Math.random() * 7)]}
        </motion.div>
      ))}

      {/* Achievement Card */}
      <motion.div
        initial={{ scale: 0, rotate: -10 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", bounce: 0.5 }}
        className="bg-white rounded-2xl p-8 shadow-2xl text-center max-w-sm mx-4"
      >
        <motion.div
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 0.5, repeat: 3 }}
          className="text-6xl mb-4"
        >
          {achievement?.icon || "🏆"}
        </motion.div>
        
        <h2 className="text-2xl font-bold text-green-800 mb-2">
          Achievement Unlocked!
        </h2>
        
        <h3 className="text-xl font-semibold mb-2">
          {achievement?.name}
        </h3>
        
        <p className="text-muted-foreground mb-4">
          {achievement?.description}
        </p>
        
        <div className="flex items-center justify-center gap-4">
          <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full font-semibold">
            +{achievement?.xp} XP
          </span>
          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
            🔬 {achievement?.stem_concept}
          </span>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Plant growth animation for individual plants
export const PlantGrowthAnimation = ({ 
  isGrowing = false, 
  plantIcon = "🌱",
  onGrowthComplete 
}) => {
  const [stage, setStage] = useState(0);

  useEffect(() => {
    if (!isGrowing) {
      setStage(0);
      return;
    }

    const growInterval = setInterval(() => {
      setStage(prev => {
        if (prev >= GROWTH_STAGES.length - 1) {
          clearInterval(growInterval);
          onGrowthComplete?.();
          return prev;
        }
        return prev + 1;
      });
    }, 800);

    return () => clearInterval(growInterval);
  }, [isGrowing, onGrowthComplete]);

  const growth = GROWTH_STAGES[stage];

  return (
    <motion.div
      animate={{ 
        scale: growth.scale,
        opacity: growth.opacity 
      }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="relative"
    >
      <span className="text-3xl">{plantIcon}</span>
      
      {isGrowing && stage < GROWTH_STAGES.length - 1 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs font-medium text-green-600 whitespace-nowrap"
        >
          {growth.label}...
        </motion.div>
      )}
      
      {stage === GROWTH_STAGES.length - 1 && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: [0, 1.5, 1] }}
          className="absolute -top-2 -right-2 text-sm"
        >
          ✨
        </motion.div>
      )}
    </motion.div>
  );
};

export default LivingGardenOverlay;
